import { gql } from "@apollo/client";
import { Database } from "@nozbe/watermelondb";
import { LayoutNodeFilterDeepNestingFragment } from "@towersystems/roam-common/lib/generated-graphql-fragment";
import {
  LayoutDeepNestingFragment,
  LayoutNodeDeepNestingFragment,
  LayoutNodeLinkDeepNestingFragment,
} from "@towersystems/roam-common/lib/generated-graphql-fragment";
import {
  Layout as LayoutEntity,
  LayoutInput,
  LayoutNodeInput,
} from "@towersystems/roam-common/lib/generated-types";
import { client } from "../../../apollo";
import Layout, { default as LayoutModel } from "../../data/model/Layout";
import LayoutNode from "../../data/model/LayoutNode";
import { AsyncLayoutActionDefinition } from "../types";

export const createLayoutMutation = gql`
  ${LayoutNodeFilterDeepNestingFragment}
  ${LayoutNodeLinkDeepNestingFragment}
  ${LayoutNodeDeepNestingFragment}
  ${LayoutDeepNestingFragment}
  mutation createLayout($input: LayoutInput!) {
    createLayout(input: $input) {
      ...LayoutDeepNesting
    }
  }
`;

export const createLayoutDefinition = new AsyncLayoutActionDefinition<
  LayoutEntity,
  LayoutModel,
  (
    database: Database,
    input: Required<LayoutInput>,
    nodesToCreate: LayoutNodeInput[]
  ) => Promise<LayoutModel>,
  (input: LayoutInput) => Promise<LayoutEntity>
>({
  localAction: async (
    database: Database,
    input: Required<LayoutInput>,
    nodesToCreate: LayoutNodeInput[]
  ) => {
    await database.write(async () => {
      return database.batch(
        database.collections.get<Layout>("layouts").prepareCreate((layout) => {
          layout._raw.id = input.id;
          layout.terminalId = input.terminalId;
        }),
        ...nodesToCreate.map((layoutKeyInput) =>
          database.collections
            .get<LayoutNode>("layout_nodes")
            .prepareCreate((layoutNode) => {
              layoutNode._raw.id = layoutKeyInput.id || "";
              layoutNode.layout.id = input.id || null;
              layoutNode.label = layoutKeyInput.label || null;
              layoutNode.color = layoutKeyInput.color;
              layoutNode.showImage = layoutKeyInput.showImage || false;
              layoutNode.productId = layoutKeyInput.productId || null;
              layoutNode.type = layoutKeyInput.type || null;
              layoutNode.position = layoutKeyInput.position || 0;
              layoutNode.action = layoutKeyInput.action || null;
              layoutNode.link = layoutKeyInput.link || null;
            })
        )
      );
    });

    return database.collections.get<LayoutModel>("layouts").find(input.id);
  },
  remoteAction: async (input: LayoutInput) => {
    return client
      .mutate({
        variables: { input },
        mutation: createLayoutMutation,
      })
      .then((result) => {
        return result.data?.createLayout;
      });
  },
});
